import { useEffect, useState, useMemo } from "react";
import { getBranchUsers } from "../../api/attendanceApi";
import { getUserSchedule } from "../../api/workingHoursApi";
import { getBranchUserRoles } from "../../api/role.api";

import StatCards from "../../components/StatCards/StatCards";
import StaffStudentToggle from "../../components/StaffStudentToggle/StaffStudentToggle";
import Filters from "../../components/Filters/Filters";
import AttendanceTable from "../../components/AttendanceTable/AttendanceTable";
import Modal from "../../components/Modal/Modal";
import RoleModal from "../../components/RoleModal/RoleModal";

import "./Dashboard.css";

export default function Dashboard() {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [date, setDate] = useState("");
    const [status, setStatus] = useState("All");
    const [type, setType] = useState("all");
    const [modalData, setModalData] = useState(null);
    const [roleModalOpen, setRoleModalOpen] = useState(false);

    const branch = JSON.parse(localStorage.getItem("branch"));

    useEffect(() => {
        loadUsers();
        loadRoles();
    }, []);

    const loadUsers = async () => {
        const data = await getBranchUsers(branch.id);

        const today = new Date().toLocaleDateString("en-US", {
            weekday: "long",
        });

        const usersWithSchedule = await Promise.all(
            data.map(async (u) => {
                const schedule = await getUserSchedule(u.id);
                return {
                    ...u,
                    schedule,
                    todayName: today,
                };
            })
        );

        setUsers(usersWithSchedule);
    };

    const loadRoles = async () => {
        const data = await getBranchUserRoles(branch.id);
        setRoles(data);
    };

    // STATUS HELPER
    function getUserStatuses(item) {
        if (!item.date || !item.date.entry_time) {
            return ["absent"];
        }

        if (item.date.status?.length > 0) {
            return item.date.status.map((s) => s.name);
        }

        return [];
    }

    // ROLE FILTER
    const roleFilteredUsers = useMemo(() => {
        if (type === "all") return users;
        return users.filter((u) => u.role_id === type);
    }, [users, type]);

    // STATS
    const stats = useMemo(() => {
        const total = roleFilteredUsers.length;

        const statusMap = {
            "came_on_time": [],
            "late": [],
            "left_early": [],
            "left_on_time": [],
            "absent": []
        };

        roleFilteredUsers.forEach((user) => {
            const statuses = getUserStatuses(user);

            if (statuses.length === 0) return;

            statuses.forEach((status) => {
                if (statusMap[status]) {
                    statusMap[status].push(user);
                }
            });
        });

        return {
            total,
            cameOnTimeCount: statusMap["came_on_time"].length,
            lateCount: statusMap["late"].length,
            leftEarlyCount: statusMap["left_early"].length,
            leftOnTimeCount: statusMap["left_on_time"].length,
            absentCount: statusMap["absent"].length,

            cameOnTimeUsers: statusMap["came_on_time"],
            lateUsers: statusMap["late"],
            leftEarlyUsers: statusMap["left_early"],
            leftOnTimeUsers: statusMap["left_on_time"],
            absentUsers: statusMap["absent"],
        };
    }, [roleFilteredUsers]);


    // TABLE FILTER
    const filteredData = useMemo(() => {
        return roleFilteredUsers.filter((item) => {

            if (status !== "All") {
                const userStatuses = getUserStatuses(item);
                if (!userStatuses.includes(status)) return false;
            }

            if (date && item.date) {
                const itemDate = new Date(item.date.date)
                    .toISOString()
                    .split("T")[0];

                if (itemDate !== date) return false;
            }

            return true;
        });
    }, [roleFilteredUsers, status, date]);

    return (
        <>
            <StatCards
                stats={stats}
                onOpen={(type) => {
                    const map = {
                        "came_on_time": {
                            title: "Came On Time Users",
                            users: stats.cameOnTimeUsers
                        },
                        "late": {
                            title: "Late Users",
                            users: stats.lateUsers
                        },
                        "left_early": {
                            title: "Left Early Users",
                            users: stats.leftEarlyUsers
                        },
                        "left_on_time": {
                            title: "Left On Time Users",
                            users: stats.leftOnTimeUsers
                        },
                        "absent": {
                            title: "Absent Users",
                            users: stats.absentUsers
                        }
                    };

                    if (map[type]) {
                        setModalData(map[type]);
                    }
                }}
            />
            {modalData && (
                <Modal
                    title={modalData.title}
                    users={modalData.users}
                    onClose={() => setModalData(null)}
                />
            )}

            <div className="filter-container">
                <StaffStudentToggle
                    value={type}
                    onChange={setType}
                    onAdd={() => setRoleModalOpen(true)}
                    roles={roles}
                />

                <Filters
                    date={date}
                    setDate={setDate}
                    status={status}
                    setStatus={setStatus}
                />
            </div>

            <AttendanceTable data={filteredData} />

            {roleModalOpen && (
                <RoleModal
                    branchId={branch?.id}
                    onClose={() => {
                        setRoleModalOpen(false);
                        loadRoles();
                    }}
                />
            )}
        </>
    );
}
